import React, { useRef, useState, useEffect } from 'react';
import sha256 from 'crypto-js/sha256';
import {
  IonPopover,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonCheckbox,
  IonIcon,
} from '@ionic/react';
import { closeOutline, folderOpenOutline } from 'ionicons/icons';

interface AddToCratePopoverProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  crates: any[];
  isLoading: boolean;
  searchText: string;
  onSearchChange: (text: string) => void;
  onCrateSelect: (crate: any) => void;
  fetchTracksOfCrate?: (crateId: string) => Promise<any[]>;
  onSubmitToTracks?: (crate: any, selectedTrackIds: string[]) => void;
}

export const AddToCratePopover: React.FC<AddToCratePopoverProps> = ({
  isOpen,
  onDidDismiss,
  crates,
  isLoading,
  searchText,
  onSearchChange,
  onCrateSelect,
  fetchTracksOfCrate,
  onSubmitToTracks,
}) => {
  const popoverRef = useRef<HTMLIonPopoverElement>(null);

  const [selectedCrate, setSelectedCrate] = useState<any | null>(null);
  const [crateTracks, setCrateTracks] = useState<any[]>([]);
  const [selectedTrackIds, setSelectedTrackIds] = useState<string[]>([]);
  const [isFetchingTracks, setIsFetchingTracks] = useState(false);
  const [trackSearchText, setTrackSearchText] = useState('');

  useEffect(() => {
    if (selectedCrate && fetchTracksOfCrate) {
      setIsFetchingTracks(true);
      setTrackSearchText('');
      fetchTracksOfCrate(selectedCrate._id)
        .then((tracks) => {
          setCrateTracks(tracks);
          setSelectedTrackIds([]);
        })
        .finally(() => setIsFetchingTracks(false));
    }
  }, [selectedCrate, fetchTracksOfCrate]);

  const handleCrateClick = (crate: any) => {
    setSelectedCrate(crate);
  };

  const handleBack = () => {
    setSelectedCrate(null);
    setCrateTracks([]);
    setSelectedTrackIds([]);
  };

  const handleCheckboxChange = (trackId: string, checked: boolean) => {
    setSelectedTrackIds((prev) => {
      if (checked) {
        return [...prev, trackId];
      } else {
        return prev.filter((id) => id !== trackId);
      }
    });
  };

  const handleSubmit = () => {
    if (selectedCrate && onSubmitToTracks) {
      onSubmitToTracks(selectedCrate, selectedTrackIds);
      onDidDismiss();
      handleBack();
    }
  };

  return (
    <IonPopover
      ref={popoverRef}
      isOpen={isOpen}
      onDidDismiss={() => {
        onDidDismiss();
        handleBack();
      }}
      className="spotify-style-popover"
    >
      <div
        className="crate-popover-container"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '50vh' }}
      >
        {!selectedCrate ? (
          <>
            <div className="popover-header">
              <div className="header-main">
                <span>ADD TO CRATE</span>
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={onDidDismiss}
                  className="close-popover-button"
                  style={{
                    margin: 0,
                    padding: 0,
                    height: '24px',
                    width: '24px',
                    '--padding-start': '0',
                    '--padding-end': '0',
                    color: 'var(--ion-color-medium)',
                  }}
                >
                  <IonIcon icon={closeOutline} style={{ fontSize: '18px' }} />
                </IonButton>
              </div>
              <IonSearchbar
                value={searchText}
                onIonInput={(e) => onSearchChange(e.detail.value!)}
                placeholder="Search crates..."
                className="crate-search-bar"
              />
            </div>
            <div
              className="popover-content"
              style={{ overflowY: 'auto', maxHeight: '40vh' }}
            >
              {isLoading ? (
                <div className="loading-container">
                  <IonSpinner name="crescent" />
                </div>
              ) : crates.length === 0 ? (
                <div className="empty-state">
                  <IonText>
                    <p className="ion-text-center">No crates found</p>
                  </IonText>
                </div>
              ) : (
                <IonList className="crate-list">
                  {crates.map((crate, index) => (
                    <IonItem
                      key={index}
                      button
                      onClick={() => handleCrateClick(crate)}
                      className="crate-item"
                    >
                      <div className="crate-art-wrapper" slot="start">
                        {crate.image ? (
                          <img
                            src={crate.image}
                            alt={crate.Title}
                            className="crate-art-image"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : null}
                        <div
                          className={`crate-art-fallback gradient-${(index % 5) + 1}`}
                        >
                          <IonIcon icon={folderOpenOutline} />
                        </div>
                      </div>
                      <IonLabel>
                        <h3>{crate.Title}</h3>
                        <p>{crate.trackCount ?? 0} tracks</p>
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="popover-header" style={{ flexShrink: 0 }}>
              <div className="header-main" style={{ paddingBottom: '0' }}>
                <IonButton
                  size="small"
                  fill="clear"
                  onClick={handleBack}
                  style={{ margin: 0, padding: 0 }}
                >
                  ← Back
                </IonButton>
                <span>Crate : {selectedCrate.Title}</span>
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={onDidDismiss}
                  className="close-popover-button"
                  style={{
                    margin: 0,
                    padding: 0,
                    height: '24px',
                    width: '24px',
                    '--padding-start': '0',
                    '--padding-end': '0',
                    color: 'var(--ion-color-medium)',
                  }}
                >
                  <IonIcon icon={closeOutline} style={{ fontSize: '18px' }} />
                </IonButton>
              </div>
            </div>

            <div
              className="popover-content"
              style={{
                flexGrow: 1,
                overflowY: 'auto',
                minHeight: 0,
                padding: '0 1rem',
              }}
            >
              {isFetchingTracks ? (
                <div className="loading-container">
                  <IonSpinner name="crescent" />
                </div>
              ) : (
                <>
                  <IonSearchbar
                    value={trackSearchText}
                    onIonInput={(e) => setTrackSearchText(e.detail.value!)}
                    placeholder="Search title or artist"
                    debounce={300}
                    className="track-search-bar"
                  />
                  <IonList>
                    <IonItem lines="none">
                      <IonCheckbox
                        slot="start"
                        checked={
                          selectedTrackIds.length === crateTracks.length &&
                          crateTracks.length > 0
                        }
                        indeterminate={
                          selectedTrackIds.length > 0 &&
                          selectedTrackIds.length < crateTracks.length
                        }
                        onIonChange={(e) => {
                          if (e.detail.checked) {
                            // IMPORTANT: Must use trimmed values to match hash generation
                            const allIds = crateTracks.map((track) =>
                              sha256(
                                JSON.stringify({
                                  artist: track.Artist?.trim() ?? '',
                                  title: track.Title?.trim() ?? '',
                                }),
                              ).toString(),
                            );
                            setSelectedTrackIds(allIds);
                          } else {
                            setSelectedTrackIds([]);
                          }
                        }}
                      />
                      <IonLabel>
                        <strong>Select All</strong>
                      </IonLabel>
                    </IonItem>
                    {crateTracks
                      .filter((track) => {
                        const lower = trackSearchText.toLowerCase();
                        return (
                          track.Title.toLowerCase().includes(lower) ||
                          track.Artist.toLowerCase().includes(lower)
                        );
                      })
                      .map((track) => {
                        // IMPORTANT: Must use trimmed values to match hash generation
                        const trackId = sha256(
                          JSON.stringify({
                            artist: track.Artist?.trim() ?? '',
                            title: track.Title?.trim() ?? '',
                          }),
                        ).toString();
                        return (
                          <IonItem key={trackId}>
                            <IonCheckbox
                              slot="start"
                              checked={selectedTrackIds.includes(trackId)}
                              onIonChange={(e) =>
                                handleCheckboxChange(trackId, e.detail.checked)
                              }
                            />
                            <IonLabel>
                              <div>{track.Title}</div>
                              <small>{track.Artist}</small>
                            </IonLabel>
                          </IonItem>
                        );
                      })}
                  </IonList>
                </>
              )}
            </div>

            <div
              className="popover-footer"
              style={{
                padding: '1rem',
                flexShrink: 0,
              }}
            >
              <IonButton
                expand="block"
                disabled={selectedTrackIds.length === 0}
                onClick={handleSubmit}
              >
                Add to Selected Track(s)
              </IonButton>
            </div>
          </>
        )}
      </div>
    </IonPopover>
  );
};
